import React, { FC, useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Box,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import styled, { keyframes } from 'styled-components';

import { makeStyles } from '@material-ui/core/styles';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { basicColor, themeColors } from "../common/colors";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';//上矢印
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';//下矢印
import { descriptionText, commentSETS } from "../common/Comment";
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import {
  LineChart,
  Line,
  LabelList,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import CommentMessages from "../components/CommentMessages";

import {
  SETSRequestType,
  SETSResponseType,
  SeTsScoreType,
} from "../common/type";
import {
  useAuth0Token,
  getSETSScore,
} from "../common/http-requests";
import { formatSeTsPeriodGradeGraduation } from "../common/util";

import {
  useUserId,
  useUserNickname,
  useLanguage,
} from "../hooks/useStoreGetters";

const BAR_CHART_SIZE: number[] = [
  1000, 320
];

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '20px',
    padding: '20px',
    overflow: 'hidden'
  },
  LineChartStyle: {
    '& > svg': {
      overflow: 'visible',
    },
    '& > svg.recharts-surface': {
      paddingBottom: '40px',
    }
  },
  LegendStyle: {
    padding: "0px",
    margin: "0px",
    textAlign: "center",
    '& > li': {
      display: "inline-block",
      marginRight: "10px;",
    },
    '& > li > span': {
      verticalAlign: "middle",
    }
  },
  tableHeadStyle: {
    textAlign: "center",
    borderRight: "solid 1px #fff",
    lineHeight: "1rem",
    padding: "10px 0",
  },
  tableCellStyle: {
    padding: "4px 16px",

  },
  tableCellChartStyle: {
    position: 'relative',
    padding: "0",
    overflow: "hidden",
  },
  accordionDetailsStyle: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center", // 水平方向の中央揃え
    justifyContent: "center", // 垂直方向の中央揃え
    minHeight: "200px", // 必要に応じて高さを設定
    width: `${BAR_CHART_SIZE[0]}px`, // 修正: 幅を親要素いっぱいに設定
    maxWidth: '100%',
    textAlign: "center", // 修正: テキストを中央揃え
    margin: "0 auto", // 修正: 要素を水平中央に配置
  },
});


const expand = (width: string) => keyframes`
  from {
    width: 0;
  }
  to {
    width: ${width};
  }
`;
interface BarChartStyleProps {
  width: string;
}
interface TableCellProps {
  group?: string;
  span?: number;
  data: { label: string; value: number; threshold: number, max: number }[];
};

interface TableCellProps {
  group?: string;
  span?: number;
  data: { label: string; value: number; threshold: number, max: number }[];
};

const BarChartStyle = styled.div<BarChartStyleProps>`
  position: absolute;
  top: calc(50% - 10px); 
  left:0;
  width: 0;
  height: 20px;
  justify-content: flex-start;
  background: ${basicColor};
  animation: ${props => expand(props.width)} .3s linear forwards;
`;

const BarChartDataPointStyle = styled.div`
  position: absolute;
  top: calc(50% - .7em); /
  left: 0;
  width: 100%;
  margin: 0 5px;
  opacity: 0;
  animation: showElement 0s forwards;
  animation-delay: .8s;
  @keyframes showElement {
  to {
    opacity: 1;
  }
}
`;

const LINE_CHART_SIZE: number[] = [
  1200, 600
];

const LineChartStyle = {
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  maxHeight: `${LINE_CHART_SIZE[1] + 50}px`,
  margin: 'auto',
};

const AccordionLabel = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderTop: `solid 1px ${themeColors[0]}`,
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
}));

const AccordionButton = styled(Box)(() => ({
  position: "absolute",
  display: "inline-block",
  top: "0",
  right: "0",
}));

const Accordion = styled(Box)(() => ({
  marginTop: "8px",
  overflow: 'hidden',
  transition: 'max-height 0.3s ease-out',
}));

const SETSPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const { getToken } = useAuth0Token();
  const userId = useUserId();
  const userNickname = useUserNickname();
  const language = useLanguage();
  const classes = useStyles();
  const { isDesktop, isMobile } = useCustomMediaQuery();

  const [isSEOpen, setIsSEOpen] = useState(true);
  const [seheight, setSEHeight] = useState('0px');
  const [isTSOpen, setIsTSOpen] = useState(true);
  const [tsheight, setTSHeight] = useState('0px');
  const [accordionHeight] = useState('auto');
  const [selectedYear, setSelectedYear] = useState<number>(0);

  const [SETSScoreData, setSETSScoreData] = useState<SeTsScoreType[]>([]);
  const [commentMessage, setCommentMessage] = useState<string>('');


  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 以下は、記録一覧画面のコードを流用した。
  // Phase1と異なり、画面遷移が必要ではないため、location（uselocation）は使用しない。
  // React18(2022年)より、strictMode（javascriptのコードを通常より厳しくエラーチェックできるモード）において
  // オフスクリーンから再度表示されることで、useEffectが２回発火（キック）される仕様変更となっため、1回に制限するよう変更した。
  // また、以下のコードの})();の();は、定義した関数を即時実行している。
  // 最後にreturn文にて定義された関数は、クリーンアップ関数であり、リスナー、オブザーバー等のオブジェクトを設定した場合、
  // レンダリング時に毎回インスタンスを作成されるため、メモリーリーク及びメモリーの圧迫を抑止するための関数。
  // useEffectの依存配列（第2引数）は、通常、空リストもしくは、具体的な変数のリストを登録するのが一般的であり、
  // 定義しない場合は、FCのstate, propsのうちいずれかが更新される度に実行されてしまうため、通常、定義しないことはない。
  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        await getSETScoreData(userId);
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getSETScoreData = async (
    login_user_id: number,
  ) => {
    setIsSending(true);
    try {
      const requestParams: SETSRequestType = {
        login_user_id,
      };
      const data: SETSResponseType = await getSETSScore(
        await getToken(),
        requestParams
      );
      const comment = commentSETS(login_user_id, userNickname, t, data);
      setSETSScoreData(data.scores);
      setCommentMessage(comment);
    } catch (e) {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    setSEHeight(isSEOpen ? 'auto' : '0px');
  }, [isSEOpen]);

  useEffect(() => {
    setTSHeight(isTSOpen ? 'auto' : '0px');
  }, [isTSOpen]);

  const [tsMax, setTsMax] = useState(0);
  useEffect(() => {
    if (SETSScoreData.length === 0) return;
    const tsSurveyValues: number[] = (Object.keys(SETSScoreData[selectedYear]) as Array<keyof SeTsScoreType>)
      .filter(key => key.startsWith('ts_survey_'))
      .map(key => SETSScoreData[selectedYear][key] as number);
    createMextData(tsSurveyValues)
    const tsValueMax = SETSScoreData.flatMap(item =>
      Object.keys(item)
        .filter(key => key.startsWith('ts_survey_'))
        .map(key => item[key as keyof SeTsScoreType] as number)
    );
    const maxValue = Math.max(...tsValueMax);
    setTsMax(Math.ceil(maxValue / 10) * 10)
  }, [SETSScoreData, selectedYear]);
  const handleSEAccordion = () => {
    setIsSEOpen(!isSEOpen);
  };

  const handleTSAccordion = () => {
    setIsTSOpen(!isTSOpen);
  };
  const CustomXAxisTick = ({ x, y, payload }: { x: number, y: number, payload: any }) => {
    const [year, grade, last] = payload.value.split('_');
    const textAnchor = 'middle';
    const fill = '#666';
    const fontSize = '1rem';
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {`${year}`}
        </text>
        <text x={0} y={0} dy={36} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {`(${grade})`}
        </text>
        <text x={0} y={0} dy={56} textAnchor={textAnchor} fill={fill} fontSize={fontSize}>
          {`${last ? last : ''}`}
        </text>
      </g>
    );
  };
  const convertData = (data: SeTsScoreType[]) =>
    data.map(entry => ({
      exam_date: `${entry.earned_period}_${entry.earned_grade}${t("common.grade")}${entry.graduation_status ? '_' + t("sets.beforeGraduation") : ''}`,
      se_survey_1: entry.se_survey_1,
    }));
  const findMaxSe = (data: SeTsScoreType[]): number => {
    const seSurvey1Values = data.map(entry => entry.se_survey_1);
    const maxValue = Math.max(...seSurvey1Values);
    const roundedValue = Math.ceil(maxValue / 10) * 10;
    return roundedValue;
  };
  const LINE_DATA_KEYS = ['se_survey_1'];
  const SERIES_NAMES_I18N = [t("sets.se_survey_1")];
  const [tsData, setTsData] = useState<any[]>([]);

  // tableのrowspanの都合上、各項目の最初の１行目はTITableFirstRowを使用
  const TITableFirstRow: React.FC<TableCellProps> = ({ group, span, data }) => {
    const isBorderBottom = data.length === 1;
    const BorderBottomStyle = isBorderBottom ? '1px solid rgba(224, 224, 224, 1)' : 'none';
    return (
      <TableRow key={group} sx={{ width: '100%' }} >
        <TableCell sx={{ padding: "4px 16px" }} rowSpan={span}>{group}</TableCell>
        <TableCell
          className={classes.tableCellStyle}
          sx={{ borderBottom: BorderBottomStyle }}>
          {data[0].label}
        </TableCell>
        <TableCell
          colSpan={2}
          sx={{ padding: 0, borderBottom: BorderBottomStyle }}
          className={classes.tableCellChartStyle}>
          <BarChartStyle width={`${((data[0].value) / tsMax) * 100}%`} />
          <BarChartDataPointStyle style={{ left: `${((data[0].value) / tsMax) * 50}%` }}>{data[0].value}</BarChartDataPointStyle>
        </TableCell>
      </TableRow>
    );
  };
  // tableのrowspanの都合上、各項目の２行目からはTITableLeftRowsを使用
  const TITableLeftRows: React.FC<TableCellProps> = ({ data }) => {
    const getBorderBottomStyle = (dataLength: number, index: number): string => {
      const isBorderBottom = index === dataLength - 1;
      return isBorderBottom ? '1px solid rgba(224, 224, 224, 1)' : 'none';
    };
    return (
      <>
        {data.map((rowData: any, rowIndex: number) => (
          rowIndex > 0 && ( // data[0]は重複するのでskip
            <TableRow key={'TITableLeftRows' + rowIndex} sx={{ width: '100%' }} >
              <TableCell
                className={classes.tableCellStyle}
                sx={{ width: '40%', borderBottom: getBorderBottomStyle(data.length, rowIndex) }}
              >
                {rowData.label}
              </TableCell>
              <TableCell
                sx={{
                  padding: 0,
                  zIndex: 5,
                  position: 'relative',
                  width: '40%',
                  borderBottom: getBorderBottomStyle(data.length, rowIndex)
                }}
                className={classes.tableCellChartStyle}
              >
                <BarChartStyle width={`${((rowData.value) / tsMax) * 100}%`} />
                <BarChartDataPointStyle style={{ left: `${((rowData.value) / tsMax) * 50}%` }}>{rowData.value}</BarChartDataPointStyle>
              </TableCell>
            </TableRow>
          )))}
      </>
    );
  };
  const createMextData = (data: number[]) => {
    const fixData = [
      {
        group: t("sets.ps4_sets_1"),
        span: 3,
        data: [
          { value: data[0], label: t("sets.ps13_sets_1") },
          { value: data[1], label: t("sets.ps13_sets_2") },
          { value: data[2], label: t("sets.ps13_sets_3") },
        ]
      },
      {
        group: t("sets.ps4_sets_2"),
        span: 3,
        data: [
          { value: data[3], label: t("sets.ps13_sets_4") },
          { value: data[4], label: t("sets.ps13_sets_5") },
          { value: data[5], label: t("sets.ps13_sets_6") },
        ]
      },
      {
        group: t("sets.ps4_sets_3"),
        span: 5,
        data: [
          { value: data[6], label: t("sets.ps13_sets_7") },
          { value: data[7], label: t("sets.ps13_sets_8") },
          { value: data[8], label: t("sets.ps13_sets_9") },
          { value: data[9], label: t("sets.ps13_sets_10") },
          { value: data[10], label: t("sets.ps13_sets_11") },
        ]
      },
      {
        group: t("sets.ps4_sets_4"),
        span: 2,
        data: [
          { value: data[11], label: t("sets.ps13_sets_12") },
          { value: data[12], label: t("sets.ps13_sets_13") },
        ]
      },
    ];
    setTsData(fixData);
  };

  const SummaryMessage = styled(Box)(() => ({
    maxWidth: `${BAR_CHART_SIZE[0]}px`,
    margin: isDesktop ? '3.5rem auto 1rem' : '7.5rem auto 1rem',
    textAlign: 'center',
  }));

  return (
    <Container maxWidth="xl">
      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <SummaryMessage>{descriptionText(userNickname, language, t, "sets")}</SummaryMessage>
            <CommentMessages message={commentMessage} />
            <Paper className={classes.chartContainer}>
              <AccordionLabel>
                <EditNoteOutlinedIcon sx={{ margin: "0 10px 0 5px" }} />
                {t("sets.chart_title_1")}
                <AccordionButton onClick={handleSEAccordion}>
                  {isSEOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </AccordionButton>
              </AccordionLabel>
              <Accordion sx={{ marginBottom: '2rem', height: isDesktop && isSEOpen ? seheight : isMobile && isSEOpen ? accordionHeight : seheight }}>
                <LineChart
                  width={LINE_CHART_SIZE[0]}
                  height={LINE_CHART_SIZE[1]}
                  data={convertData(SETSScoreData)}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 0,
                  }}
                  className={classes.LineChartStyle}
                  style={LineChartStyle}
                >
                  <XAxis
                    dataKey="exam_date"
                    tick={<CustomXAxisTick x={0} y={0} payload={SETSScoreData} />}
                    interval={0}
                    padding={{ left: 40, right: 40 }} />
                  <YAxis domain={[0, findMaxSe(SETSScoreData)]} ticks={Array.from({ length: Math.floor(findMaxSe(SETSScoreData) / 10) }, (_, i) => (i + 1) * 10)} />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ width: "100%", bottom: "5px", fontSize: "max(1.2vw, .8rem)" }}
                  /** @カスタムDOT 
                   payload={NCBTScoreData.seriesNameList} 
                   content={renderLegend}
                  */
                  />
                  {
                    LINE_DATA_KEYS.map((labelName, index) => (
                      <>
                        <defs>
                          <linearGradient
                            id="gradationColor"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="0%"
                          >
                            <stop offset="0%" stopColor={themeColors[0]} />
                            <stop offset="100%" stopColor={themeColors[1]} />
                          </linearGradient>
                        </defs>
                        <Line
                          key={labelName + index}
                          type="linear"
                          dataKey={labelName}
                          name={SERIES_NAMES_I18N[index]}
                          fill={themeColors[0]}
                          stroke="url(#gradationColor)"
                          activeDot={{ r: 8 }}
                          strokeWidth={2}
                        /** @カスタムDOT 
                        dot={renderLineMarker} 
                        */
                        >
                          <LabelList
                            dataKey={labelName}
                            position="insideTop"
                            offset={10}
                            fill="url(#gradationColor)"
                            style={{ fontSize: isDesktop ? '1.2rem' : '2.8rem' }}
                          />
                        </Line>
                      </>
                    ))
                  }
                </LineChart>
              </Accordion>
              <AccordionLabel sx={{ margin: "20px 0 0 0" }}>
                <AccessibilityNewOutlinedIcon sx={{ margin: "0 10px 0 5px" }} />
                {t("sets.chart_title_2")}
                <AccordionButton onClick={handleTSAccordion}>
                  {isTSOpen ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </AccordionButton>
              </AccordionLabel>

              <Accordion sx={{ height: isDesktop && isTSOpen ? tsheight : isMobile && isTSOpen ? accordionHeight : tsheight }}>
                <Grid
                  container
                  mt={2}
                  maxWidth={`${LINE_CHART_SIZE[0]}px`}
                  mx={'auto'}
                  wrap={'nowrap'}
                  justifyContent={"center"}>
                  {SETSScoreData.map((item: SeTsScoreType, index: number) => (
                    <Grid
                      key={'SETSScoreData_Accordion' + index}
                      sx={{ paddingLeft: 0 }}
                      m={isDesktop ? 0.5 : 0.1}
                      textAlign={'center'}
                      alignItems={"center"}
                      justifyContent={"center"}
                      item
                      xs={12 / SETSScoreData.length}>
                      <Button
                        key={item.earned_period + index}
                        onClick={() => setSelectedYear(index)}
                        style={{
                          background: selectedYear === index ? `${basicColor}` : '#fff',
                          color: selectedYear === index ? '#fff' : `${themeColors[0]}`,
                          minWidth: 'auto',
                          padding: isDesktop ? '6px 8px' : '2px',
                          textTransform: "none"
                        }}
                      >
                        {isDesktop ? (
                          formatSeTsPeriodGradeGraduation(item.earned_period, item.earned_grade, item.graduation_status, t)
                        ) : (
                          <>
                            {item.earned_period}<br />
                            ({item.earned_grade}{t("common.grade")})<br />
                            {item.graduation_status ? t("sets.beforeGraduation") : ''}
                          </>
                        )}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
                <AccordionDetails className={classes.accordionDetailsStyle}>
                  <TableContainer sx={{ marginTop: '1rem' }}>
                    <Table aria-label="simple table">
                      <TableHead key="table-head" sx={{ padding: 0 }}>
                        <TableRow key="table-row" >
                          <TableCell />
                          <TableCell />
                          <TableCell>
                            <Grid container spacing={2} mt={2} mb={-2}>
                              <Grid item xs={0} width={0} textAlign={'left'} p={0} mr={-2}>
                                <Typography fontSize={'.9em'} mr={2} ml={-2}>0</Typography>
                              </Grid>
                              {(Array.from({ length: tsMax / 10 }, (_, i) => (i + 1) * 10)).map((number) => (
                                <Grid item xs={12 / (tsMax / 10)} key={'Typography' + number} textAlign={'right'}>
                                  <Typography fontSize={'.9em'} mr={-1.5} ml={1.5}>{number}</Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody key="table-body">
                        {tsData && tsData.map((item: any, index: number) => (
                          <React.Fragment key={`${item.group}-${index}`}>
                            <TITableFirstRow key={`${item.group}-${index}`} group={item.group} span={item.span} data={item.data} />
                            <TITableLeftRows key={`${item.data}-${index}`} data={item.data} />
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </>
        )}
      <Toolbar />
    </Container>
  );
};

export default SETSPage;
